import { Minister } from "@/models/minister"
import { CredentialRequest } from "@/models/credentialRequest";
import { Database } from "@/types/supabase";
import {
    getCredentialRequestsPaginated,
    getCredentialRequestById,
    createCredentialRequest,
    updateCredentialRequest,
    deleteCredentialRequest,
    getMinistersByCredentialRequest,
    addMinisterToCredentialRequest,
    addMinistersToCredentialRequest,
    removeMinisterFromCredentialRequest,
    getCredentialRequestsForMinister,
    submitCredentialRequest,
    approveCredentialRequest,
    rejectCredentialRequest,
    markCredentialRequestAsPrinted,
    markCredentialRequestAsDelivered,
    getPersonsByCredentialRequest,
    addPersonToCredentialRequest,
    removePersonFromCredentialRequest,
    createInstitutionCredentialRequest,
    createNacionalFunctionRequest,
    syncNacionalFunctionRequest,
    type CredentialRequestFilters
} from "@/Services/credentials.service";
import { type PaginatedResponse } from "@/Services/supabaseClient";

type CredentialRequestInsert = Database['public']['Tables']['credential_requests']['Insert'];
type CredentialRequestUpdate = Database['public']['Tables']['credential_requests']['Update'];

// ============================================================================
// CREDENTIAL REQUESTS CRUD
// ============================================================================

const getRequestsPaginated = async (
    page: number = 1,
    pageSize: number = 20,
    filters?: CredentialRequestFilters,
    sort?: { field: string; direction: 'asc' | 'desc' }
): Promise<PaginatedResponse<CredentialRequest>> => {
    try {
        return await getCredentialRequestsPaginated(page, pageSize, filters, sort);
    } catch (error) {
        console.error('Error getting paginated credential requests:', error);
        return {
            data: [],
            count: 0,
            page,
            pageSize,
            totalPages: 0
        };
    }
}

const getRequestById = async (id: string): Promise<CredentialRequest | null> => {
    try {
        return await getCredentialRequestById(id);
    } catch (error) {
        console.error('Error getting credential request by id:', error);
        return null;
    }
}

const createRequest = async (request: CredentialRequestInsert): Promise<CredentialRequest | null> => {
    try {
        return await createCredentialRequest(request);
    } catch (error) {
        console.error('Error creating credential request:', error);
        throw error;
    }
}

const updateRequest = async (id: string, updates: CredentialRequestUpdate): Promise<CredentialRequest | null> => {
    try {
        return await updateCredentialRequest(id, updates);
    } catch (error) {
        console.error('Error updating credential request:', error);
        throw error;
    }
}

const deleteRequest = async (id: string): Promise<boolean> => {
    try {
        await deleteCredentialRequest(id);
        return true;
    } catch (error) {
        console.error('Error deleting credential request:', error);
        return false;
    }
}

// ============================================================================
// MINISTERS IN CREDENTIAL REQUESTS (Junction Table)
// ============================================================================

const getCredentialReqMinisters = async (requestId: string): Promise<Minister[]> => {
    try {
        return await getMinistersByCredentialRequest(requestId);
    } catch (error) {
        console.error('Error getting ministers for credential request:', error);
        return [];
    }
}

const addMinisterToRequest = async (requestId: string, ministerId: string): Promise<boolean> => {
    try {
        await addMinisterToCredentialRequest(requestId, ministerId);
        return true;
    } catch (error) {
        console.error('Error adding minister to credential request:', error);
        return false;
    }
}

const addMinistersToRequest = async (requestId: string, ministerIds: string[]): Promise<{ success: boolean; count: number }> => {
    try {
        const result = await addMinistersToCredentialRequest(requestId, ministerIds);
        return { success: true, count: result.length };
    } catch (error) {
        console.error('Error adding ministers to credential request:', error);
        return { success: false, count: 0 };
    }
}

const removeMinisterFromRequest = async (requestId: string, ministerId: string): Promise<boolean> => {
    try {
        await removeMinisterFromCredentialRequest(requestId, ministerId);
        return true;
    } catch (error) {
        console.error('Error removing minister from credential request:', error);
        return false;
    }
}

const getRequestsForMinister = async (ministerId: string): Promise<CredentialRequest[]> => {
    try {
        return await getCredentialRequestsForMinister(ministerId);
    } catch (error) {
        console.error('Error getting credential requests for minister:', error);
        return [];
    }
}

// ============================================================================
// WORKFLOW STATE TRANSITIONS
// ============================================================================

const submitRequest = async (id: string): Promise<CredentialRequest | null> => {
    try {
        return await submitCredentialRequest(id);
    } catch (error) {
        console.error('Error submitting credential request:', error);
        throw error;
    }
}

const approveRequest = async (id: string, userId: string): Promise<CredentialRequest | null> => {
    try {
        return await approveCredentialRequest(id, userId);
    } catch (error) {
        console.error('Error approving credential request:', error);
        throw error;
    }
}

const rejectRequest = async (id: string, userId: string, observations?: string): Promise<CredentialRequest | null> => {
    try {
        return await rejectCredentialRequest(id, userId, observations);
    } catch (error) {
        console.error('Error rejecting credential request:', error);
        throw error;
    }
}

const markAsPrinted = async (id: string, userId: string): Promise<CredentialRequest | null> => {
    try {
        return await markCredentialRequestAsPrinted(id, userId);
    } catch (error) {
        console.error('Error marking credential request as printed:', error);
        throw error;
    }
}

const markAsDelivered = async (id: string, userId: string): Promise<CredentialRequest | null> => {
    try {
        return await markCredentialRequestAsDelivered(id, userId);
    } catch (error) {
        console.error('Error marking credential request as delivered:', error);
        throw error;
    }
}

export {
    // CRUD operations
    getRequestsPaginated,
    getRequestById,
    createRequest,
    updateRequest,
    deleteRequest,
    
    // Junction table operations
    getCredentialReqMinisters,
    addMinisterToRequest,
    addMinistersToRequest,
    removeMinisterFromRequest,
    getRequestsForMinister,
    
    // Workflow state transitions
    submitRequest,
    approveRequest,
    rejectRequest,
    markAsPrinted,
    markAsDelivered,
    
    // Institution credential request operations
    getPersonsByCredentialRequest,
    addPersonToCredentialRequest,
    removePersonFromCredentialRequest,
    createInstitutionCredentialRequest,

    // Nacional credential request operations
    createNacionalFunctionRequest,
    syncNacionalFunctionRequest,

    // Type exports
    type CredentialRequestFilters,
    type PaginatedResponse,
}